import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import LanguagePopup from './components/LanguagePopup';

import './styles/globals.css';

// Eager — used on every page (layout)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import PageLoader from './components/PageLoader';
import ScrollToTop from './components/ScrollToTop';
import CookieBanner from './components/CookieBanner';

// ─── Lazy-loaded public pages ─────────────────────────────
const Home            = lazy(() => import('./pages/Home'));
const About           = lazy(() => import('./pages/About'));
const Services        = lazy(() => import('./pages/Services'));
const ServiceDetail   = lazy(() => import('./pages/services/ServiceDetail'));
const Portfolio       = lazy(() => import('./pages/Portfolio'));
const ProjectDetail   = lazy(() => import('./pages/portfolio/ProjectDetail'));
const Contact         = lazy(() => import('./pages/Contact'));
const Devis           = lazy(() => import('./pages/Devis'));
const Apporteurs      = lazy(() => import('./pages/Apporteurs'));
const Questionnaire   = lazy(() => import('./pages/Questionnaire'));
const Presentation    = lazy(() => import('./pages/Presentation'));
const Connect         = lazy(() => import('./pages/Connect'));
const NotFound        = lazy(() => import('./pages/NotFound'));
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const RoueDeLaChance  = lazy(() => import('./pages/RoueDeLaChance'));

// ─── Lazy-loaded dashboard ────────────────────────────────
const DashboardLayout  = lazy(() => import('./dashboard/DashboardLayout'));
const DashboardHome    = lazy(() => import('./dashboard/DashboardHome'));
const Login            = lazy(() => import('./dashboard/pages/Login'));
const ManageProjects   = lazy(() => import('./dashboard/pages/ManageProjects'));
const Contacts         = lazy(() => import('./dashboard/pages/Contacts'));
const DevisRequests    = lazy(() => import('./dashboard/pages/DevisRequests'));
const Questionnaires   = lazy(() => import('./dashboard/pages/Questionnaires'));
const DashApporteurs   = lazy(() => import('./dashboard/pages/Apporteurs'));
const BankAccounts     = lazy(() => import('./dashboard/pages/BankAccounts'));
const DashTransactions = lazy(() => import('./dashboard/pages/Transactions'));
const Reports          = lazy(() => import('./dashboard/pages/Reports'));
const Team             = lazy(() => import('./dashboard/pages/Team'));
const BoardsIndex      = lazy(() => import('./dashboard/pages/Boards'));
const BoardDetail      = lazy(() => import('./dashboard/pages/BoardDetail'));
const Users            = lazy(() => import('./dashboard/pages/Users'));
const MyTasks          = lazy(() => import('./dashboard/pages/MyTasks'));
const Settings         = lazy(() => import('./dashboard/pages/Settings'));
const RoleGuard        = lazy(() => import('./dashboard/RoleGuard'));

// Apporteurs d'affaires
const ApporteurDashboard       = lazy(() => import('./dashboard/pages/ApporteurDashboard'));
const ApporteurProspects       = lazy(() => import('./dashboard/pages/ApporteurProspects'));
const ApporteurClients         = lazy(() => import('./dashboard/pages/ApporteurClients'));
const AdminApporteursRegistry  = lazy(() => import('./dashboard/pages/AdminApporteursRegistry'));
const EventLeads               = lazy(() => import('./dashboard/pages/EventLeads'));
const RoueParticipants         = lazy(() => import('./dashboard/pages/RoueParticipants'));
const SocietesProspection      = lazy(() => import('./dashboard/pages/SocietesProspection'));

function PublicLayout({ children }) {
  return (
    <>
      <LanguagePopup />
      <Navbar />
      <main>{children}</main>
      <Footer />
      <WhatsAppButton />
      <CookieBanner />
    </>
  );
}

export default function App() {
  // Le PageLoader artificiel de 1.8s a été retiré : Suspense affiche déjà le PageLoader
  // pendant le chargement des chunks lazy, ce qui donne le même effet visuel mais
  // seulement le temps nécessaire (typiquement <500ms au lieu de 1800ms fixes).
  return (
    <HelmetProvider>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public site */}
                <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                <Route path="/a-propos" element={<PublicLayout><About /></PublicLayout>} />
                <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
                <Route path="/services/:slug" element={<PublicLayout><ServiceDetail /></PublicLayout>} />
                <Route path="/portfolio" element={<PublicLayout><Portfolio /></PublicLayout>} />
                <Route path="/portfolio/:id" element={<PublicLayout><ProjectDetail /></PublicLayout>} />
                <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                <Route path="/devis" element={<PublicLayout><Devis /></PublicLayout>} />
                <Route path="/apporteurs" element={<PublicLayout><Apporteurs /></PublicLayout>} />
                <Route path="/questionnaire" element={<PublicLayout><Questionnaire /></PublicLayout>} />
                <Route path="/presentation" element={<PublicLayout><Presentation /></PublicLayout>} />
                <Route path="/connect" element={<PublicLayout><Connect /></PublicLayout>} />
                <Route path="/mentions-legales" element={<PublicLayout><MentionsLegales /></PublicLayout>} />
                <Route path="/roue" element={<PublicLayout><RoueDeLaChance /></PublicLayout>} />

                {/* Dashboard auth */}
                <Route path="/dashboard/login" element={<Login />} />

                {/* Dashboard protected */}
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<DashboardHome />} />

                  {/* User-accessible */}
                  <Route path="mes-taches" element={<MyTasks />} />
                  <Route path="taches" element={<BoardsIndex />} />
                  <Route path="taches/:boardId" element={<BoardDetail />} />

                  {/* Apporteur d'affaires — son espace dédié */}
                  <Route path="apporteur" element={<RoleGuard role="apporteur"><ApporteurDashboard /></RoleGuard>} />
                  <Route path="apporteur/prospects" element={<RoleGuard role="apporteur"><ApporteurProspects /></RoleGuard>} />
                  <Route path="apporteur/clients" element={<RoleGuard role="apporteur"><ApporteurClients /></RoleGuard>} />

                  {/* Admin — registre centralisé des apports */}
                  <Route path="registre-apporteurs" element={<RoleGuard role="admin"><AdminApporteursRegistry /></RoleGuard>} />
                  <Route path="event-leads" element={<RoleGuard role="admin"><EventLeads /></RoleGuard>} />
                  <Route path="roue-participants" element={<RoleGuard role="admin"><RoueParticipants /></RoleGuard>} />
                  <Route path="societes-prospection" element={<RoleGuard role="admin"><SocietesProspection /></RoleGuard>} />

                  {/* Admin-only */}
                  <Route path="projets" element={<RoleGuard role="admin"><ManageProjects /></RoleGuard>} />
                  <Route path="contacts" element={<RoleGuard role="admin"><Contacts /></RoleGuard>} />
                  <Route path="devis" element={<RoleGuard role="admin"><DevisRequests /></RoleGuard>} />
                  <Route path="questionnaires" element={<RoleGuard role="admin"><Questionnaires /></RoleGuard>} />
                  <Route path="apporteurs" element={<RoleGuard role="admin"><DashApporteurs /></RoleGuard>} />
                  <Route path="comptes" element={<RoleGuard role="admin"><BankAccounts /></RoleGuard>} />
                  <Route path="encaissements" element={<RoleGuard role="admin"><DashTransactions transactionType="encaissement" /></RoleGuard>} />
                  <Route path="decaissements" element={<RoleGuard role="admin"><DashTransactions transactionType="decaissement" /></RoleGuard>} />
                  <Route path="rapports" element={<RoleGuard role="admin"><Reports /></RoleGuard>} />
                  <Route path="equipe" element={<RoleGuard role="admin"><Team /></RoleGuard>} />
                  <Route path="utilisateurs" element={<RoleGuard role="admin"><Users /></RoleGuard>} />
                  <Route path="parametres" element={<RoleGuard role="admin"><Settings /></RoleGuard>} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
              </Routes>
            </Suspense>

            <ToastContainer
              position="bottom-right"
              autoClose={3500}
              hideProgressBar={false}
              theme="dark"
              toastStyle={{ background: '#0f3033', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12 }}
            />
          </BrowserRouter>
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
